// UvunajiDummyProducts.js — multi-image gallery admin
// BASE URL fix: API calls -> /uvunaji/api/..., Images -> /media/... (no /uvunaji)

import React, { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Tag, Package, ShoppingCart, Plus, Edit2, Trash2, Search,
  ChevronLeft, ChevronRight, Upload, X, Check, ArrowLeft,
  AlertTriangle, RefreshCw, Filter, Star, Layers, DollarSign,
  Image as ImageIcon, Crown, ZoomIn,
} from 'lucide-react';

const NAV  = '#001f3f';
const TEAL = '#0d9488';

// API calls go to /uvunaji/api/v1/...
const API_BASE = 'https://api.jamboricg.com/uvunaji';
// Images/media files are at root domain (no /uvunaji)
const IMG_BASE = 'https://api.jamboricg.com';

const getToken = () => localStorage.getItem('token') || '';

const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${getToken()}`, ...opts.headers },
    ...opts,
  });
  if (res.status === 204) return null;
  const data = await res.json();
  if (!res.ok) throw data;
  return data;
};

const api = {
  getCategories:  ()          => apiFetch('/api/v1/dummy/categories/?all=true'),
  createCategory: (b)         => apiFetch('/api/v1/dummy/categories/',        { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(b) }),
  updateCategory: (id,b)      => apiFetch(`/api/v1/dummy/categories/${id}/`,  { method:'PUT',  headers:{'Content-Type':'application/json'}, body:JSON.stringify(b) }),
  deleteCategory: (id)        => apiFetch(`/api/v1/dummy/categories/${id}/`,  { method:'DELETE' }),

  getProducts:    ()          => apiFetch('/api/v1/dummy/products/?all=true'),
  createProduct:  (fd)        => apiFetch('/api/v1/dummy/products/add/',           { method:'POST', body:fd }),
  updateProduct:  (id,fd)     => apiFetch(`/api/v1/dummy/products/${id}/update/`,  { method:'PUT',  body:fd }),
  deleteProduct:  (id)        => apiFetch(`/api/v1/dummy/products/${id}/delete/`,  { method:'DELETE' }),

  getImages:      (id)        => apiFetch(`/api/v1/dummy/products/${id}/images/`),
  uploadImages:   (id,fd)     => apiFetch(`/api/v1/dummy/products/${id}/images/`,             { method:'POST', body:fd }),
  deleteImage:    (pid,iid)   => apiFetch(`/api/v1/dummy/products/${pid}/images/${iid}/`,     { method:'DELETE' }),
  setPrimary:     (pid,iid)   => apiFetch(`/api/v1/dummy/products/${pid}/images/${iid}/set-primary/`, { method:'POST' }),

  getOrders:      ()          => apiFetch('/api/v1/dummy/admin/orders/'),
  updateStatus:   (id,status) => apiFetch(`/api/v1/dummy/admin/orders/${id}/status/`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({status}) }),
};

// Get full image URL — image_url from backend is already absolute
// If only relative path available, prepend IMG_BASE (not API_BASE)
const imgUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url; // already absolute — use directly
  return `${IMG_BASE}${url}`;             // relative path — prepend base domain only
};

const fmt  = n => parseFloat(n||0).toLocaleString();
const fmtD = d => d ? new Date(d).toLocaleDateString() : '—';

const STATUS_OPTIONS = ['pending','confirmed','processing','shipped','delivered','cancelled'];
const STATUS_STYLE = {
  pending:   'bg-amber-100 text-amber-700 border border-amber-200',
  confirmed: 'bg-blue-100 text-blue-700 border border-blue-200',
  processing:'bg-violet-100 text-violet-700 border border-violet-200',
  shipped:   'bg-indigo-100 text-indigo-700 border border-indigo-200',
  delivered: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  cancelled: 'bg-red-100 text-red-700 border border-red-200',
};
const DELIVERY_STYLE = { pickup:'bg-slate-100 text-slate-600', deliver:'bg-teal-100 text-teal-700' };
const PAYMENT_STYLE  = { bank:'bg-blue-50 text-blue-700', mobile:'bg-green-50 text-green-700', cash:'bg-amber-50 text-amber-700' };

// ─── Shared UI ────────────────────────────────────────────────────────────────
const Badge = ({ label, cls }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${cls}`}>
    {String(label||'').replace(/_/g,' ')}
  </span>
);

const StatCard = ({ icon:Icon, label, value, sub, color='teal' }) => {
  const G = { teal:'from-teal-500 to-teal-600', navy:'from-[#001f3f] to-[#002d5a]', gold:'from-amber-400 to-amber-500', green:'from-green-500 to-green-600', red:'from-red-500 to-red-600' };
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${G[color]||G.teal} flex items-center justify-center shadow-md flex-shrink-0`}>
        <Icon size={22} className="text-white"/>
      </div>
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-slate-800 leading-tight">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

const Toast = ({ msg, type, onClose }) => (
  <div className={`fixed bottom-5 right-5 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl text-white text-sm font-medium ${type==='success'?'bg-green-600':'bg-red-600'}`}>
    {type==='success'?<Check size={16}/>:<AlertTriangle size={16}/>}
    {msg}
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100"><X size={13}/></button>
  </div>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-20 text-teal-600 gap-3">
    <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"/>
    <span className="text-sm font-medium">Loading...</span>
  </div>
);

const Field = ({ label, name, type='text', value, onChange, error, placeholder, prefix, hint, rows }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">{label}</label>
    {hint && <p className="text-[10px] text-slate-400 mb-1">{hint}</p>}
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-medium z-10">{prefix}</span>}
      {rows ? (
        <textarea name={name} value={value} onChange={onChange} placeholder={placeholder} rows={rows}
          className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none ${error?'border-red-400 bg-red-50':'border-slate-200 bg-white'}`}/>
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
          step={type==='number'?'any':undefined}
          className={`w-full ${prefix?'pl-14':'pl-3'} pr-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 ${error?'border-red-400 bg-red-50':'border-slate-200 bg-white'}`}/>
      )}
    </div>
    {error && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertTriangle size={10}/>{error}</p>}
  </div>
);

const PrimaryBtn = ({ children, onClick, type='button', disabled, className='' }) => (
  <button type={type} onClick={onClick} disabled={disabled}
    className={`flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl hover:opacity-90 disabled:opacity-50 transition-all ${className}`}
    style={{ background:`linear-gradient(135deg,${NAV},${TEAL})` }}>
    {children}
  </button>
);

const Pagination = ({ page, totalPages, total, perPage, onPrev, onNext }) => (
  <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
    <span className="text-xs text-slate-400">{page*perPage+1}–{Math.min((page+1)*perPage,total)} of {total}</span>
    <div className="flex items-center gap-1">
      <button onClick={onPrev} disabled={page===0} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg disabled:opacity-40"><ChevronLeft size={15}/></button>
      <span className="text-xs font-medium text-slate-600 px-2">{page+1}/{totalPages||1}</span>
      <button onClick={onNext} disabled={page>=totalPages-1} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg disabled:opacity-40"><ChevronRight size={15}/></button>
    </div>
  </div>
);

const TH = ({ children }) => (
  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap bg-slate-50">{children}</th>
);

// ─── Lightbox ────────────────────────────────────────────────────────────────
const Lightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const img = images[idx];
  const src = imgUrl(img.image_url || img.image);
  return (
    <div className="fixed inset-0 z-[200] bg-black/92 flex items-center justify-center" onClick={onClose}>
      <div className="relative max-w-4xl w-full mx-4" onClick={e=>e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white"><X size={24}/></button>
        <img src={src} alt={img.caption||'Product'} className="w-full max-h-[80vh] object-contain rounded-xl"
          onError={e=>{ e.target.src='data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23334155"/><text x="50%" y="50%" fill="%2394a3b8" text-anchor="middle" dy=".3em">Image unavailable</text></svg>'; }}/>
        {img.caption && <p className="text-center text-white/70 text-sm mt-3">{img.caption}</p>}
        <div className="flex items-center justify-center gap-4 mt-4">
          <button onClick={()=>setIdx(i=>Math.max(0,i-1))} disabled={idx===0} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl disabled:opacity-30 text-white"><ChevronLeft size={20}/></button>
          <span className="text-white/60 text-sm">{idx+1} / {images.length}</span>
          <button onClick={()=>setIdx(i=>Math.min(images.length-1,i+1))} disabled={idx===images.length-1} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl disabled:opacity-30 text-white"><ChevronRight size={20}/></button>
        </div>
        <div className="flex justify-center gap-2 mt-3">
          {images.map((_,i)=>(
            <button key={i} onClick={()=>setIdx(i)} className={`h-1.5 rounded-full transition-all ${i===idx?'bg-white w-6':'bg-white/40 w-1.5'}`}/>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Image Gallery Manager ───────────────────────────────────────────────────
const ImageGalleryManager = ({ productId, productName, onClose, onToast }) => {
  const qc = useQueryClient();
  const { data:images=[], isLoading, refetch } = useQuery({
    queryKey: ['dummyImages', productId],
    queryFn:  () => api.getImages(productId),
  });

  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [caption,     setCaption]     = useState('');
  const [newFiles,    setNewFiles]    = useState([]);
  const [previews,    setPreviews]    = useState([]);
  const fileRef = useRef();

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files);
    setNewFiles(files);
    setPreviews(files.map(f => URL.createObjectURL(f)));
  };

  const handleUpload = async () => {
    if (!newFiles.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      // Send each file as a separate 'image' field
      newFiles.forEach(f => fd.append('image', f));
      if (caption) fd.append('caption', caption);
      await api.uploadImages(productId, fd);
      qc.invalidateQueries(['dummyImages', productId]);
      qc.invalidateQueries(['dummyProds']);
      refetch();
      setNewFiles([]); setPreviews([]); setCaption('');
      if (fileRef.current) fileRef.current.value = '';
      onToast(`${newFiles.length} image(s) uploaded successfully!`, 'success');
    } catch (e) {
      console.error('Upload error:', e);
      onToast('Failed to upload images. Check file size and format.', 'error');
    } finally { setUploading(false); }
  };

  const setPrimaryMut = useMutation({
    mutationFn: (imgId) => api.setPrimary(productId, imgId),
    onSuccess:  () => { qc.invalidateQueries(['dummyImages', productId]); qc.invalidateQueries(['dummyProds']); refetch(); onToast('Primary image updated!', 'success'); },
    onError:    () => onToast('Failed to set primary image.', 'error'),
  });

  const deleteMut = useMutation({
    mutationFn: (imgId) => api.deleteImage(productId, imgId),
    onSuccess:  () => { qc.invalidateQueries(['dummyImages', productId]); qc.invalidateQueries(['dummyProds']); refetch(); onToast('Image deleted.', 'success'); },
    onError:    () => onToast('Failed to delete image.', 'error'),
  });

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background:`linear-gradient(135deg,${NAV},${TEAL})`}}>
                <ImageIcon size={16} className="text-white"/>
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">Product Images</h3>
                <p className="text-xs text-slate-400">{productName} — {images.length} image(s)</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={18}/></button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Upload section */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
              <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Upload size={13}/> UPLOAD NEW IMAGES
              </h4>
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl py-8 px-4 cursor-pointer hover:border-teal-400 hover:bg-teal-50/20 transition-all group mb-3">
                <div className="w-12 h-12 rounded-xl bg-white group-hover:bg-teal-50 border border-slate-200 flex items-center justify-center mb-3 shadow-sm transition-colors">
                  <Upload size={20} className="text-slate-400 group-hover:text-teal-600"/>
                </div>
                <p className="text-sm font-semibold text-slate-600">Click to select images</p>
                <p className="text-xs text-slate-400 mt-1">JPEG, PNG or GIF · Multiple files allowed</p>
                <input ref={fileRef} type="file" hidden accept="image/*" multiple onChange={handleFilePick}/>
              </label>

              {/* Preview selected */}
              {previews.length > 0 && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-3">
                  {previews.map((src, i) => (
                    <div key={i} className="relative group">
                      <img src={src} alt="" className="w-full h-16 object-cover rounded-lg border border-slate-200"/>
                      <button
                        onClick={() => { const nf=[...newFiles]; nf.splice(i,1); setNewFiles(nf); const np=[...previews]; np.splice(i,1); setPreviews(np); }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={10} className="text-white"/>
                      </button>
                      {i===0 && images.length===0 && (
                        <span className="absolute bottom-0 left-0 right-0 text-center text-[9px] font-bold bg-teal-500 text-white rounded-b-lg py-0.5">Primary</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-3">
                <input type="text" value={caption} onChange={e=>setCaption(e.target.value)}
                  placeholder="Caption / alt text (optional — applies to all selected)"
                  className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white"/>
                <PrimaryBtn onClick={handleUpload} disabled={!newFiles.length || uploading} className="flex-shrink-0">
                  {uploading
                    ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Uploading...</>
                    : <><Upload size={14}/>Upload{newFiles.length>0?` (${newFiles.length})`:''}</>
                  }
                </PrimaryBtn>
              </div>
            </div>

            {/* Current images */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                  <ImageIcon size={13}/> CURRENT IMAGES ({images.length})
                </h4>
                <button onClick={()=>refetch()} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><RefreshCw size={13}/></button>
              </div>

              {isLoading ? <Spinner/> : images.length===0 ? (
                <div className="text-center py-10 text-slate-400">
                  <ImageIcon size={36} className="mx-auto mb-2 opacity-30"/>
                  <p className="text-sm">No images yet — upload some above</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {images.map((img, idx) => {
                    // Use image_url (absolute) if available, else build from IMG_BASE
                    const src = imgUrl(img.image_url || img.image);
                    return (
                      <div key={img.id}
                        className={`relative group rounded-xl overflow-hidden border-2 transition-all ${img.is_primary?'border-teal-400 shadow-teal-100 shadow-lg':'border-slate-200 hover:border-slate-300'}`}>
                        <div className="relative h-36 bg-slate-100">
                          <img
                            src={src}
                            alt={img.caption||'Product'}
                            className="w-full h-full object-cover"
                            onError={e=>{
                              e.target.onerror=null;
                              e.target.src='data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150"><rect fill="%23e2e8f0"/><text x="50%" y="50%" fill="%2394a3b8" text-anchor="middle" font-size="12" dy=".3em">Image error</text></svg>';
                            }}
                          />
                          {img.is_primary && (
                            <div className="absolute top-2 left-2 flex items-center gap-1 bg-teal-500 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow">
                              <Crown size={9}/> Primary
                            </div>
                          )}
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                            <button onClick={()=>setLightboxIdx(idx)} className="p-2 bg-white/90 hover:bg-white rounded-xl text-slate-700 shadow" title="View fullscreen">
                              <ZoomIn size={14}/>
                            </button>
                            {!img.is_primary && (
                              <button onClick={()=>setPrimaryMut.mutate(img.id)} className="p-2 bg-teal-500 hover:bg-teal-600 rounded-xl text-white shadow" title="Set as primary">
                                <Crown size={14}/>
                              </button>
                            )}
                            <button
                              onClick={()=>{ if(window.confirm('Delete this image?')) deleteMut.mutate(img.id); }}
                              className="p-2 bg-red-500 hover:bg-red-600 rounded-xl text-white shadow" title="Delete">
                              <Trash2 size={14}/>
                            </button>
                          </div>
                        </div>
                        <div className="p-2 bg-white">
                          <p className="text-xs text-slate-500 truncate">{img.caption || <span className="text-slate-300 italic">No caption</span>}</p>
                          <p className="text-[10px] text-slate-300 mt-0.5">#{img.id} · order {img.sort_order}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-slate-100 flex justify-end bg-slate-50/50 flex-shrink-0">
            <button onClick={onClose} className="px-5 py-2.5 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors">
              Close Gallery
            </button>
          </div>
        </div>
      </div>

      {lightboxIdx !== null && (
        <Lightbox images={images} startIndex={lightboxIdx} onClose={()=>setLightboxIdx(null)}/>
      )}
    </>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — CATEGORIES
// ══════════════════════════════════════════════════════════════════════════════
function CategoriesTab({ onToast }) {
  const qc = useQueryClient();
  const { data:cats=[], isLoading, refetch } = useQuery({ queryKey:['dummyCats'], queryFn:api.getCategories });

  const EMPTY = { name:'', icon:'', description:'', sort_order:0, is_active:true };
  const [form,   setForm]   = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => { const e={}; if(!form.name.trim()) e.name='Required'; setErrors(e); return !Object.keys(e).length; };

  const saveMut = useMutation({
    mutationFn: editId ? d=>api.updateCategory(editId,d) : api.createCategory,
    onSuccess:  () => { qc.invalidateQueries(['dummyCats']); refetch(); setForm(EMPTY); setEditId(null); setErrors({}); onToast(editId?'Category updated!':'Category created!','success'); },
    onError:    () => onToast('Failed to save category.','error'),
  });

  const delMut = useMutation({
    mutationFn: api.deleteCategory,
    onSuccess:  () => { qc.invalidateQueries(['dummyCats']); refetch(); onToast('Category deleted.','success'); },
    onError:    () => onToast('Cannot delete — products may still be linked.','error'),
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Tag}    label="Total"   value={cats.length}                         color="navy"/>
        <StatCard icon={Check}  label="Active"  value={cats.filter(c=>c.is_active).length} color="teal"/>
        <StatCard icon={Layers} label="Products" value={cats.reduce((s,c)=>s+(c.product_count||0),0)} color="gold"/>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sticky top-4">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background:`linear-gradient(135deg,${NAV},${TEAL})`}}>
                {editId?<Edit2 size={14} className="text-white"/>:<Plus size={14} className="text-white"/>}
              </div>
              <h3 className="text-sm font-bold text-slate-800">{editId?'Edit Category':'Add Category'}</h3>
            </div>
            <form onSubmit={e=>{e.preventDefault();if(validate())saveMut.mutate(form);}} className="space-y-4">
              <Field label="Category Name *" name="name" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} error={errors.name} placeholder="e.g. Ardhi"/>
              <Field label="Mobile Icon" name="icon" value={form.icon} onChange={e=>setForm(p=>({...p,icon:e.target.value}))} placeholder="e.g. terrain" hint="MaterialCommunityIcons name for mobile chip"/>
              <Field label="Description" name="description" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={2}/>
              <Field label="Sort Order" name="sort_order" type="number" value={form.sort_order} onChange={e=>setForm(p=>({...p,sort_order:parseInt(e.target.value)||0}))} hint="Lower = appears first on mobile"/>
              <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e=>setForm(p=>({...p,is_active:e.target.checked}))} className="w-4 h-4 text-teal-600 rounded"/>
                Active (visible on mobile)
              </label>
              <div className="flex gap-2 pt-2">
                <PrimaryBtn type="submit" disabled={saveMut.isLoading} className="flex-1">
                  {saveMut.isLoading?<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Saving...</>:<><Check size={14}/>{editId?'Update':'Add Category'}</>}
                </PrimaryBtn>
                {editId && <button type="button" onClick={()=>{setEditId(null);setForm(EMPTY);setErrors({});}} className="px-4 py-2.5 text-sm border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50">Cancel</button>}
              </div>
            </form>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-800">All Categories ({cats.length})</h3>
              <button onClick={()=>refetch()} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><RefreshCw size={14}/></button>
            </div>
            {isLoading?<Spinner/>:(
              <table className="min-w-full">
                <thead><tr className="border-b border-slate-100">{['Name','Slug','Icon','Products','Order','Status','Actions'].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {cats.length===0?(
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400">No categories yet — add one on the left</td></tr>
                  ):cats.map(cat=>(
                    <tr key={cat.id} className={`hover:bg-slate-50/60 transition-colors group ${editId===cat.id?'bg-teal-50/30':''}`}>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-800">{cat.name}</td>
                      <td className="px-4 py-3"><code className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg">{cat.slug}</code></td>
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono">{cat.icon||'—'}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-teal-50 text-teal-700 text-sm font-bold border border-teal-100">{cat.product_count??0}</span></td>
                      <td className="px-4 py-3 text-sm text-slate-600">{cat.sort_order}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cat.is_active?'bg-green-100 text-green-700':'bg-red-100 text-red-600'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cat.is_active?'bg-green-500':'bg-red-400'}`}/>{cat.is_active?'Active':'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={()=>{setEditId(cat.id);setForm({name:cat.name,icon:cat.icon||'',description:cat.description||'',sort_order:cat.sort_order||0,is_active:cat.is_active});}} className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg"><Edit2 size={13}/></button>
                          <button onClick={()=>{if(window.confirm(`Delete "${cat.name}"?`))delMut.mutate(cat.id);}} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — PRODUCTS
// ══════════════════════════════════════════════════════════════════════════════
function ProductsTab({ onToast }) {
  const qc = useQueryClient();
  const { data:products=[], isLoading:loadP, refetch:refetchP } = useQuery({ queryKey:['dummyProds'], queryFn:api.getProducts });
  const { data:categories=[] }                                   = useQuery({ queryKey:['dummyCats'],  queryFn:api.getCategories });

  const [view,          setView]          = useState('list');
  const [editItem,      setEditItem]      = useState(null);
  const [galleryProduct,setGalleryProduct]= useState(null);
  const [search,        setSearch]        = useState('');
  const [catFilter,     setCatFilter]     = useState('all');
  const [page,          setPage]          = useState(0);
  const PER = 8;

  const EMPTY = { name:'', description:'', category:'', price:'', stock:'', unit:'unit', discount:'', rating:'4.0', is_featured:false, is_active:true };
  const [form,   setForm]   = useState(EMPTY);
  const [image,  setImage]  = useState(null);
  const [preview,setPreview]= useState(null);
  const [errors, setErrors] = useState({});
  const fileRef = useRef();

  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const validate = () => {
    const e={};
    if(!form.name.trim())  e.name='Required';
    if(!form.category)     e.category='Select a category';
    if(!form.price)        e.price='Required';
    if(form.stock==='')    e.stock='Required';
    if(!editItem&&!image)  e.image='Image required for new products';
    setErrors(e); return !Object.keys(e).length;
  };

  const buildFD = () => {
    const fd=new FormData();
    Object.entries(form).forEach(([k,v])=>{ if(v!==''&&v!==null&&v!==undefined) fd.append(k,v); });
    if(image) fd.append('image',image);
    return fd;
  };

  const createMut = useMutation({ mutationFn:()=>api.createProduct(buildFD()), onSuccess:()=>{ qc.invalidateQueries(['dummyProds']); refetchP(); resetForm(); setView('list'); onToast('Product added!','success'); }, onError:()=>onToast('Failed to add product.','error') });
  const updateMut = useMutation({ mutationFn:()=>api.updateProduct(editItem.id,buildFD()), onSuccess:()=>{ qc.invalidateQueries(['dummyProds']); refetchP(); resetForm(); setView('list'); onToast('Product updated!','success'); }, onError:()=>onToast('Failed to update product.','error') });
  const delMut    = useMutation({ mutationFn:api.deleteProduct, onSuccess:()=>{ qc.invalidateQueries(['dummyProds']); refetchP(); onToast('Product deleted.','success'); }, onError:()=>onToast('Failed to delete.','error') });

  const resetForm = () => { setForm(EMPTY); setImage(null); setPreview(null); setErrors({}); setEditItem(null); if(fileRef.current) fileRef.current.value=''; };
  const openEdit  = p => { setEditItem(p); setForm({name:p.name,description:p.description||'',category:String(p.category),price:p.price,stock:p.stock,unit:p.unit||'unit',discount:p.discount||'',rating:p.rating||'4.0',is_featured:p.is_featured,is_active:p.is_active}); setImage(null); setPreview(null); setView('edit'); };

  // Get the primary image src for a product — use image_url if absolute
  const getPrimaryImg = (p) => {
    if (p.primary_image) return imgUrl(p.primary_image);
    if (p.images && p.images.length > 0) {
      const pri = p.images.find(i => i.is_primary) || p.images[0];
      return imgUrl(pri.image_url || pri.image);
    }
    if (p.image) return imgUrl(p.image);
    return null;
  };

  const filtered   = products.filter(p=>catFilter==='all'||String(p.category)===catFilter).filter(p=>!search||p.name?.toLowerCase().includes(search.toLowerCase()));
  const paginated  = filtered.slice(page*PER,(page+1)*PER);
  const totalPages = Math.ceil(filtered.length/PER);

  // Form view
  if(view==='create'||view==='edit') return (
    <div className="max-w-3xl mx-auto">
      <button onClick={()=>{resetForm();setView('list');}} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-5"><ArrowLeft size={15}/> Back to Products</button>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{background:`linear-gradient(135deg,${NAV},${TEAL})`}}>
            {view==='edit'?<Edit2 size={16} className="text-white"/>:<Plus size={16} className="text-white"/>}
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-800">{view==='edit'?`Edit: ${editItem?.name}`:'Add New Product'}</h2>
            <p className="text-xs text-slate-400">{view==='edit'?'After saving, click the 📷 Images button in the table to manage the full gallery.':'Fill required fields. Add more images after saving via the 📷 button.'}</p>
          </div>
        </div>
        <form onSubmit={e=>{e.preventDefault();if(!validate())return;editItem?updateMut.mutate():createMut.mutate();}} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Product Name *" name="name" value={form.name} onChange={e=>set('name',e.target.value)} error={errors.name} placeholder="e.g. Cargo Jeans"/>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Category *</label>
              <select value={form.category} onChange={e=>set('category',e.target.value)}
                className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white ${errors.category?'border-red-400 bg-red-50':'border-slate-200'}`}>
                <option value="">— Select Category —</option>
                {categories.map(c=><option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </select>
              {errors.category&&<p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertTriangle size={10}/>{errors.category}</p>}
            </div>
            <Field label="Price (TZS) *" name="price" type="number" value={form.price} onChange={e=>set('price',e.target.value)} error={errors.price} prefix="TZS" placeholder="0.00"/>
            <Field label="Stock Units *"  name="stock" type="number" value={form.stock} onChange={e=>set('stock',e.target.value)} error={errors.stock} placeholder="e.g. 50"/>
            <Field label="Unit Label" name="unit" value={form.unit} onChange={e=>set('unit',e.target.value)} placeholder="e.g. unit, ekari, kipande"/>
            <Field label="Discount %" name="discount" type="number" value={form.discount} onChange={e=>set('discount',e.target.value)} placeholder="e.g. 15"/>
            <Field label="Rating (1–5)" name="rating" type="number" value={form.rating} onChange={e=>set('rating',e.target.value)}/>
            <div className="flex flex-col justify-center gap-3">
              <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer"><input type="checkbox" checked={form.is_featured} onChange={e=>set('is_featured',e.target.checked)} className="w-4 h-4 text-teal-600 rounded"/>Featured (mobile strip)</label>
              <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={e=>set('is_active',e.target.checked)} className="w-4 h-4 text-teal-600 rounded"/>Active (visible on mobile)</label>
            </div>
          </div>
          <Field label="Description" name="description" value={form.description} onChange={e=>set('description',e.target.value)} placeholder="Full product description..." rows={3}/>
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Cover Image {!editItem&&<span className="text-red-500">*</span>}
              {editItem&&<span className="font-normal text-slate-400 ml-1">(blank = keep existing)</span>}
            </label>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-xl py-7 cursor-pointer hover:border-teal-400 hover:bg-teal-50/20 transition-all group">
              <Upload size={20} className="text-slate-400 group-hover:text-teal-600 mb-2"/>
              <p className="text-sm font-medium text-slate-600">Click to upload cover image</p>
              <p className="text-xs text-slate-400 mt-0.5">JPEG, PNG or GIF</p>
              <input ref={fileRef} type="file" hidden accept="image/*" onChange={e=>{const f=e.target.files[0];if(f){setImage(f);setPreview(URL.createObjectURL(f));}}}/>
            </label>
            {errors.image&&<p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertTriangle size={10}/>{errors.image}</p>}
            {preview&&(
              <div className="mt-3 flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                <div className="w-16 h-16 rounded-lg overflow-hidden border"><img src={preview} className="w-full h-full object-cover"/></div>
                <div className="flex-1"><p className="text-xs font-semibold text-slate-700 truncate">{image?.name}</p><p className="text-xs text-green-600 mt-0.5">✓ New cover selected</p></div>
                <button type="button" onClick={()=>{setImage(null);setPreview(null);if(fileRef.current)fileRef.current.value='';}} className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-lg"><X size={14}/></button>
              </div>
            )}
            {view==='edit'&&!preview&&(()=>{const src=getPrimaryImg(editItem);return src?(
              <div className="mt-3 flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="w-16 h-16 rounded-lg overflow-hidden border border-blue-200"><img src={src} className="w-full h-full object-cover" alt="current" onError={e=>{e.target.style.display='none';}}/></div>
                <div><p className="text-xs font-semibold text-blue-700">Current cover image</p><p className="text-xs text-blue-500 mt-0.5">Upload above to replace · manage all via 📷 Images button</p></div>
              </div>
            ):null;})()}
          </div>
          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <button type="button" onClick={()=>{resetForm();setView('list');}} className="px-5 py-2.5 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
            <PrimaryBtn type="submit" disabled={createMut.isLoading||updateMut.isLoading}>
              {(createMut.isLoading||updateMut.isLoading)?<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Saving...</>:<><Check size={15}/>{view==='edit'?'Update Product':'Add Product'}</>}
            </PrimaryBtn>
          </div>
        </form>
      </div>
    </div>
  );

  // List view
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package}       label="Total"        value={products.length}                          color="navy"/>
        <StatCard icon={Star}          label="Featured"     value={products.filter(p=>p.is_featured).length} color="gold"/>
        <StatCard icon={Check}         label="Active"       value={products.filter(p=>p.is_active).length}   color="teal"/>
        <StatCard icon={AlertTriangle} label="Out of Stock" value={products.filter(p=>p.stock===0).length} sub="stock=0" color="red"/>
      </div>
      <div className="flex items-center justify-between">
        <div><h3 className="text-base font-bold text-slate-800">Products</h3><p className="text-xs text-slate-400 mt-0.5">Hover a row → click 📷 to manage images per product</p></div>
        <div className="flex gap-2">
          <button onClick={()=>refetchP()} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400"><RefreshCw size={15}/></button>
          <PrimaryBtn onClick={()=>{resetForm();setView('create');}}><Plus size={14}/> Add Product</PrimaryBtn>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input type="text" placeholder="Search products..." value={search} onChange={e=>{setSearch(e.target.value);setPage(0);}}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:bg-white"/>
          </div>
          <div className="flex items-center gap-2">
            <Filter size={13} className="text-slate-400"/>
            <select value={catFilter} onChange={e=>{setCatFilter(e.target.value);setPage(0);}}
              className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-300">
              <option value="all">All Categories</option>
              {categories.map(c=><option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <p className="mt-3 pt-3 border-t border-slate-50 text-xs text-slate-400">
          Showing <span className="font-semibold text-slate-600">{filtered.length}</span> of {products.length} products
        </p>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loadP?<Spinner/>:(
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead><tr className="border-b border-slate-100">{['Images','Name','Category','Price (TZS)','Stock','Discount','Rating','Featured','Status','Actions'].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
              <tbody className="divide-y divide-slate-50">
                {paginated.length===0?(
                  <tr><td colSpan={10} className="px-4 py-14 text-center"><Package size={32} className="mx-auto mb-2 text-slate-200"/><p className="text-sm text-slate-400">No products found</p></td></tr>
                ):paginated.map(p=>{
                  const primarySrc = getPrimaryImg(p);
                  const imgCount   = p.images?.length || 0;
                  return (
                    <tr key={p.id} className="hover:bg-slate-50/60 transition-colors group">
                      <td className="px-4 py-3">
                        <div className="relative w-14 h-14">
                          <div className="w-14 h-14 rounded-xl overflow-hidden border-2 border-slate-200 bg-slate-100">
                            {primarySrc
                              ? <img src={primarySrc} alt={p.name} className="w-full h-full object-cover"
                                  onError={e=>{e.target.onerror=null;e.target.style.display='none';}}/>
                              : <div className="w-full h-full flex items-center justify-center"><ImageIcon size={18} className="text-slate-300"/></div>
                            }
                          </div>
                          {imgCount>0&&(
                            <div className="absolute -bottom-1 -right-1 bg-teal-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-white shadow min-w-[18px] text-center">
                              {imgCount}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3"><p className="text-sm font-semibold text-slate-800 max-w-[140px] truncate">{p.name}</p><p className="text-[10px] text-slate-400">#{p.id} · {p.unit}</p></td>
                      <td className="px-4 py-3"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">{p.category_name||'—'}</span></td>
                      <td className="px-4 py-3 text-sm font-bold text-slate-800 whitespace-nowrap">
                        {fmt(p.price)}
                        {p.discount&&<span className="ml-1 text-xs text-green-600">→{fmt(p.discounted_price)}</span>}
                      </td>
                      <td className="px-4 py-3"><span className={`text-sm font-bold ${p.stock===0?'text-red-600':p.stock<10?'text-amber-600':'text-green-600'}`}>{p.stock}</span></td>
                      <td className="px-4 py-3 text-sm text-slate-600">{p.discount?`${p.discount}%`:'—'}</td>
                      <td className="px-4 py-3"><div className="flex items-center gap-1"><Star size={12} className="text-amber-400 fill-amber-400"/><span className="text-xs font-semibold">{p.rating}</span></div></td>
                      <td className="px-4 py-3"><span className={`text-xs font-bold ${p.is_featured?'text-amber-600':'text-slate-300'}`}>{p.is_featured?'⭐ Yes':'No'}</span></td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${p.is_active?'bg-green-100 text-green-700':'bg-red-100 text-red-600'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${p.is_active?'bg-green-500':'bg-red-400'}`}/>{p.is_active?'Active':'Hidden'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={()=>setGalleryProduct({id:p.id,name:p.name})} className="p-1.5 hover:bg-teal-50 text-teal-600 rounded-lg" title="Manage Images"><ImageIcon size={13}/></button>
                          <button onClick={()=>openEdit(p)} className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg" title="Edit"><Edit2 size={13}/></button>
                          <button onClick={()=>{if(window.confirm(`Delete "${p.name}"?`))delMut.mutate(p.id);}} disabled={delMut.isLoading} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg disabled:opacity-50"><Trash2 size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length>PER&&<Pagination page={page} totalPages={totalPages} total={filtered.length} perPage={PER} onPrev={()=>setPage(p=>p-1)} onNext={()=>setPage(p=>p+1)}/>}
      </div>
      {galleryProduct&&(
        <ImageGalleryManager
          productId={galleryProduct.id}
          productName={galleryProduct.name}
          onClose={()=>{setGalleryProduct(null);refetchP();}}
          onToast={onToast}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 3 — ORDERS
// ══════════════════════════════════════════════════════════════════════════════
function OrdersTab({ onToast }) {
  const qc = useQueryClient();
  const { data:orders=[], isLoading, refetch } = useQuery({ queryKey:['dummyOrders'], queryFn:api.getOrders });
  const [search,sS]=useState(''); const [statusFilter,sSF]=useState('all'); const [delivFilter,sDF]=useState('all'); const [payFilter,sPF]=useState('all'); const [expanded,sE]=useState(null); const [page,sP]=useState(0);
  const PER=10;
  const statusMut=useMutation({ mutationFn:({id,status})=>api.updateStatus(id,status), onSuccess:()=>{qc.invalidateQueries(['dummyOrders']);refetch();onToast('Status updated!','success');}, onError:()=>onToast('Failed to update.','error') });
  const filtered=orders.filter(o=>statusFilter==='all'||o.status===statusFilter).filter(o=>delivFilter==='all'||o.delivery_mode===delivFilter).filter(o=>payFilter==='all'||o.payment_method===payFilter).filter(o=>!search||o.order_reference?.toLowerCase().includes(search.toLowerCase())||o.product_name?.toLowerCase().includes(search.toLowerCase())||o.user_name?.toLowerCase().includes(search.toLowerCase()));
  const paginated=filtered.slice(page*PER,(page+1)*PER); const totalPages=Math.ceil(filtered.length/PER);
  const revenue=orders.reduce((s,o)=>s+parseFloat(o.total_price||0),0);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ShoppingCart} label="Total" value={orders.length} color="navy"/>
        <StatCard icon={AlertTriangle} label="Pending" value={orders.filter(o=>o.status==='pending').length} sub="need action" color="gold"/>
        <StatCard icon={Check} label="Delivered" value={orders.filter(o=>o.status==='delivered').length} color="teal"/>
        <StatCard icon={DollarSign} label="Revenue" value={`TZS ${fmt(revenue.toFixed(0))}`} sub="all orders" color="green"/>
      </div>
      <div className="flex items-center justify-between">
        <div><h3 className="text-base font-bold text-slate-800">Orders</h3><p className="text-xs text-slate-400 mt-0.5">All orders from mobile Urithi Tradable screen</p></div>
        <button onClick={()=>refetch()} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400"><RefreshCw size={15}/></button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-48"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/><input type="text" placeholder="Search ref, product, customer..." value={search} onChange={e=>{sS(e.target.value);sP(0);}} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:bg-white"/></div>
          <select value={statusFilter} onChange={e=>{sSF(e.target.value);sP(0);}} className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-300"><option value="all">All Statuses</option>{STATUS_OPTIONS.map(s=><option key={s} value={s} className="capitalize">{s}</option>)}</select>
          <select value={delivFilter} onChange={e=>{sDF(e.target.value);sP(0);}} className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-300"><option value="all">All Delivery</option><option value="pickup">Kuja Kuchukua</option><option value="deliver">Niletee</option></select>
          <select value={payFilter} onChange={e=>{sPF(e.target.value);sP(0);}} className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-300"><option value="all">All Payments</option><option value="bank">Benki</option><option value="mobile">Simu</option><option value="cash">Taslimu</option></select>
        </div>
        <p className="mt-3 pt-3 border-t border-slate-50 text-xs text-slate-400">Showing <span className="font-semibold text-slate-600">{filtered.length}</span> of {orders.length} orders</p>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {isLoading?<Spinner/>:(
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead><tr className="border-b border-slate-100">{['Ref','Customer','Product','Qty','Total','Delivery','Payment','Status','Date',''].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
              <tbody className="divide-y divide-slate-50">
                {paginated.length===0?(
                  <tr><td colSpan={10} className="px-4 py-14 text-center"><ShoppingCart size={32} className="mx-auto mb-2 text-slate-200"/><p className="text-sm text-slate-400">No orders found</p></td></tr>
                ):paginated.map(order=>(
                  <React.Fragment key={order.id}>
                    <tr className={`hover:bg-slate-50/60 transition-colors cursor-pointer ${expanded===order.id?'bg-teal-50/20':''}`} onClick={()=>sE(expanded===order.id?null:order.id)}>
                      <td className="px-4 py-3.5"><code className="text-xs font-bold text-teal-700 bg-teal-50 border border-teal-100 px-2 py-1 rounded-lg">{order.order_reference}</code></td>
                      <td className="px-4 py-3.5"><p className="text-sm font-semibold text-slate-800">{order.user_name||'—'}</p>{order.user_phone&&<p className="text-xs text-slate-400">{order.user_phone}</p>}</td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          {order.product_image&&<img src={imgUrl(order.product_image)} alt="" className="w-8 h-8 rounded-lg object-cover border border-slate-200 flex-shrink-0" onError={e=>{e.target.style.display='none';}}/>}
                          <p className="text-sm text-slate-700 max-w-[110px] truncate font-medium">{order.product_name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm font-bold text-slate-800">{order.quantity}</td>
                      <td className="px-4 py-3.5 text-sm font-bold text-slate-800 whitespace-nowrap">{fmt(order.total_price)}</td>
                      <td className="px-4 py-3.5"><Badge label={order.delivery_label||order.delivery_mode} cls={DELIVERY_STYLE[order.delivery_mode]||'bg-slate-100 text-slate-600'}/></td>
                      <td className="px-4 py-3.5"><Badge label={order.payment_label||order.payment_method} cls={PAYMENT_STYLE[order.payment_method]||'bg-slate-100 text-slate-600'}/></td>
                      <td className="px-4 py-3.5" onClick={e=>e.stopPropagation()}>
                        <select value={order.status} onChange={e=>statusMut.mutate({id:order.id,status:e.target.value})}
                          className={`text-xs font-semibold border rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-300 capitalize cursor-pointer ${STATUS_STYLE[order.status]||'bg-slate-100 text-slate-600 border-slate-200'}`}>
                          {STATUS_OPTIONS.map(s=><option key={s} value={s} className="capitalize bg-white text-slate-800">{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-slate-500 whitespace-nowrap">{fmtD(order.created_at)}</td>
                      <td className="px-4 py-3.5 text-slate-300"><ChevronRight size={15} className={`transition-transform ${expanded===order.id?'rotate-90':''}`}/></td>
                    </tr>
                    {expanded===order.id&&(
                      <tr className="bg-slate-50/50">
                        <td colSpan={10} className="px-6 py-5">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white rounded-xl border border-slate-200 p-4">
                              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${order.delivery_mode==='deliver'?'bg-teal-500':'bg-slate-300'}`}/>{order.delivery_mode==='deliver'?'Delivery Address':'Pickup'}</p>
                              {order.delivery_mode==='deliver'?(
                                [['Recipient',order.recipient_name],['Phone',order.recipient_phone],['Region',order.region],['Address',order.address],['Landmark',order.landmark],['Risk Accepted',order.risk_accepted?'✓ Yes':'✗ No']].map(([l,v])=>v?(
                                  <div key={l} className="flex justify-between items-start py-1.5 border-b border-slate-50 last:border-0 gap-2"><span className="text-xs text-slate-400 font-medium flex-shrink-0">{l}</span><span className="text-xs font-semibold text-slate-700 text-right">{v}</span></div>
                                ):null)
                              ):<p className="text-xs text-slate-400 italic">Customer collects from office.</p>}
                            </div>
                            <div className="bg-white rounded-xl border border-slate-200 p-4">
                              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Order Details</p>
                              {[['Unit Price',`TZS ${fmt(order.unit_price)}`],['Quantity',order.quantity],['Total',`TZS ${fmt(order.total_price)}`],['Payment',order.payment_label||order.payment_method],['Delivery',order.delivery_label||order.delivery_mode],['Placed',fmtD(order.created_at)],['Updated',fmtD(order.updated_at)]].map(([l,v])=>(
                                <div key={l} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0"><span className="text-xs text-slate-400 font-medium">{l}</span><span className="text-xs font-semibold text-slate-700">{v}</span></div>
                              ))}
                            </div>
                            <div className="bg-white rounded-xl border border-slate-200 p-4">
                              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Notes</p>
                              <p className="text-sm text-slate-600 min-h-[40px]">{order.notes||<span className="text-slate-300 italic text-xs">No notes</span>}</p>
                              <div className="mt-4 pt-3 border-t border-slate-100">
                                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Update Status</p>
                                <select value={order.status} onChange={e=>statusMut.mutate({id:order.id,status:e.target.value})} className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white capitalize">
                                  {STATUS_OPTIONS.map(s=><option key={s} value={s} className="capitalize">{s}</option>)}
                                </select>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length>PER&&<Pagination page={page} totalPages={totalPages} total={filtered.length} perPage={PER} onPrev={()=>sP(p=>p-1)} onNext={()=>sP(p=>p+1)}/>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT EXPORT
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id:'categories', label:'Categories', icon:Tag },
  { id:'products',   label:'Products',   icon:Package },
  { id:'orders',     label:'Orders',     icon:ShoppingCart },
];

export default function UvunajiDummyProducts() {
  const [activeTab,setActiveTab]=useState('categories');
  const [toast,setToast]=useState(null);
  const showToast=(msg,type='success')=>{setToast({msg,type});setTimeout(()=>setToast(null),4500);};
  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-100">
          {TABS.map(t=>{const Icon=t.icon;const active=activeTab===t.id;return(
            <button key={t.id} onClick={()=>setActiveTab(t.id)}
              className={`flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold flex-1 border-b-2 transition-all ${active?'border-teal-500 text-teal-600 bg-teal-50/30':'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
              <Icon size={16} className={active?'text-teal-500':'text-slate-400'}/>{t.label}
              {active&&<span className="ml-1 px-2 py-0.5 rounded text-xs font-bold bg-teal-100 text-teal-700">Active</span>}
            </button>
          );})}
        </div>
        <div className="px-6 py-3 bg-slate-50/50 border-b border-slate-100">
          <p className="text-xs text-slate-400">
            {activeTab==='categories'&&'📁 Manage categories — become filter chips on mobile. Icon = MaterialCommunityIcons name.'}
            {activeTab==='products'&&'📦 Manage products — hover a row, click 📷 to upload and manage multiple images per product.'}
            {activeTab==='orders'&&'🛒 All orders from mobile — click a row to expand, update status inline or in expanded view.'}
          </p>
        </div>
      </div>
      {activeTab==='categories'&&<CategoriesTab onToast={showToast}/>}
      {activeTab==='products'  &&<ProductsTab   onToast={showToast}/>}
      {activeTab==='orders'    &&<OrdersTab     onToast={showToast}/>}
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
}